import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Spinner, Alert } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { useAuth } from '../../contexts/AuthContext';
import CurrencyFormatter from '../../utils/CurrencyFormatter';
import BankAccountsApprovalSection from './BankAccountsApprovalSection';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { API_URL } from '../../config/api';

// Registrar componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
);

const AdminDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao carregar dados da dashboard');
      }

      const data = await response.json();
      console.log('📊 Dashboard Admin Data:', data);
      setDashboardData(data);
    } catch (error) {
      console.error('Erro ao carregar dashboard admin:', error);
      setError('Erro ao carregar dados da dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Configurações dos gráficos
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return CurrencyFormatter.centsToFormatted(value);
          }
        }
      }
    }
  };

  // Dados simulados mais realistas para demonstração
  const lineChartData = {
    labels: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
    datasets: [
      {
        label: 'Vendas (R$)',
        data: [45000, 52000, 48000, 61000, 55000, 67000, 72000, 68000, 75000, 82000, 78000, 85000],
        borderColor: '#3b82f6', // Azul mais vibrante
        backgroundColor: 'rgba(59, 130, 246, 0.15)', // Azul com mais transparência
        tension: 0.4,
        fill: true
      },
      {
        label: 'Comissões (R$)',
        data: [4500, 5200, 4800, 6100, 5500, 6700, 7200, 6800, 7500, 8200, 7800, 8500],
        borderColor: '#ff6b35', // Laranja mais vivo
        backgroundColor: 'rgba(255, 107, 53, 0.15)', // Laranja mais vivo com transparência
        tension: 0.4,
        fill: true
      }
    ]
  };

  const barChartData = {
    labels: dashboardData?.topOwners?.slice(0, 5).map(owner => owner.name) || ['Produtor A', 'Produtor B', 'Produtor C', 'Produtor D', 'Produtor E'],
    datasets: [
      {
        label: 'Vendas por Produtor',
        data: dashboardData?.topOwners?.slice(0, 5).map(owner => owner.sales_count) || [25, 18, 32, 15, 28],
        backgroundColor: [
          'rgba(255, 99, 132, 0.8)',
          'rgba(54, 162, 235, 0.8)',
          'rgba(255, 205, 86, 0.8)',
          'rgba(75, 192, 192, 0.8)',
          'rgba(153, 102, 255, 0.8)',
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 205, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
        ],
        borderWidth: 1
      }
    ]
  };

  // Gráfico de pizza para categorias de produtos
  const categoryPieData = {
    labels: ['Cursos Online', 'E-books', 'Consultorias', 'Softwares', 'Outros'],
    datasets: [
      {
        data: [35, 25, 20, 15, 5],
        backgroundColor: [
          'rgba(255, 99, 132, 0.8)',
          'rgba(54, 162, 235, 0.8)',
          'rgba(255, 205, 86, 0.8)',
          'rgba(75, 192, 192, 0.8)',
          'rgba(153, 102, 255, 0.8)',
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 205, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
        ],
        borderWidth: 2
      }
    ]
  };

  // Gráfico de donut para status de vendas
  const statusDonutData = {
    labels: ['Concluídas', 'Pendentes', 'Canceladas', 'Reembolsadas'],
    datasets: [
      {
        data: [75, 15, 7, 3],
        backgroundColor: [
          'rgba(40, 167, 69, 0.8)',
          'rgba(255, 193, 7, 0.8)',
          'rgba(220, 53, 69, 0.8)',
          'rgba(108, 117, 125, 0.8)',
        ],
        borderColor: [
          'rgba(40, 167, 69, 1)',
          'rgba(255, 193, 7, 1)',
          'rgba(220, 53, 69, 1)',
          'rgba(108, 117, 125, 1)',
        ],
        borderWidth: 2
      }
    ]
  };

  // Gráfico de barras horizontais para afiliados
  const affiliateBarData = {
    labels: dashboardData?.topAffiliates?.slice(0, 5).map(affiliate => affiliate.name) || ['Afiliado A', 'Afiliado B', 'Afiliado C', 'Afiliado D', 'Afiliado E'],
    datasets: [
      {
        label: 'Comissões (R$)',
        data: dashboardData?.topAffiliates?.slice(0, 5).map(affiliate => affiliate.total_commissions / 100) || [2500, 1800, 3200, 1500, 2800],
        backgroundColor: [
          'rgba(255, 99, 132, 0.8)',
          'rgba(54, 162, 235, 0.8)',
          'rgba(255, 205, 86, 0.8)',
          'rgba(75, 192, 192, 0.8)',
          'rgba(153, 102, 255, 0.8)',
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 205, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
        ],
        borderWidth: 1
      }
    ]
  };

  const doughnutData = {
    labels: ['Comissões Pagas', 'Receita da Plataforma', 'Taxas de Processamento', 'Outros'],
    datasets: [
      {
        data: [
          (dashboardData?.totalCommissions || 0) / 100 || 15000,
          (dashboardData?.platformEarnings || 0) / 100 || 8000,
          3000,
          2000
        ],
        backgroundColor: [
          'rgba(255, 193, 7, 0.8)',
          'rgba(13, 202, 240, 0.8)',
          'rgba(40, 167, 69, 0.8)',
          'rgba(108, 117, 125, 0.8)',
        ],
        borderColor: [
          'rgba(255, 193, 7, 1)',
          'rgba(13, 202, 240, 1)',
          'rgba(40, 167, 69, 1)',
          'rgba(108, 117, 125, 1)',
        ],
        borderWidth: 2
      }
    ]
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Carregando dashboard administrativo...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="m-4">
        <Icon icon="mdi:alert-circle" className="me-2" />
        Erro ao carregar dashboard: {error}
      </Alert>
    );
  }

  if (!dashboardData) {
    return (
      <Alert variant="warning" className="m-4">
        <Icon icon="mdi:information" className="me-2" />
        Nenhum dado disponível no momento.
      </Alert>
    );
  }

  return (
    <div className="container-fluid p-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Dashboard Administrativo</h2>
          <p className="text-muted mb-0">Visão geral da plataforma</p>
        </div>
        <div className="d-flex align-items-center">
          <Icon icon="mdi:shield-account" className="text-primary me-2" style={{ fontSize: '2rem' }} />
          <div>
            <div className="fw-semibold">Administrador</div>
            <small className="text-muted">{user?.name}</small>
          </div>
        </div>
      </div>

      {/* Linha branca de separação */}
      <hr className="my-4" style={{ borderColor: 'white', borderWidth: '2px' }} />

      {/* Cards de Estatísticas - Mesmo padrão da Dashboard normal */}
      <Row className="mb-4">
        <Col md={3} className="mb-3">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="d-flex align-items-center justify-content-center mb-3">
                <div className="bg-primary bg-opacity-10 rounded-circle p-3">
                  <Icon icon="mdi:shopping" className="text-primary" style={{ fontSize: '2rem' }} />
                </div>
              </div>
              <h4 className="text-primary mb-1">
                {(dashboardData.totalSales || 0).toLocaleString()}
              </h4>
              <p className="text-muted mb-0">Total de Vendas</p>
              <small className="text-success">
                <Icon icon="mdi:trending-up" className="me-1" />
                {(dashboardData.salesStats?.growth || 0)}% vs mês anterior
              </small>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3} className="mb-3">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="d-flex align-items-center justify-content-center mb-3">
                <div className="bg-success bg-opacity-10 rounded-circle p-3">
                  <Icon icon="mdi:currency-usd" className="text-success" style={{ fontSize: '2rem' }} />
                </div>
              </div>
              <h4 className="text-success mb-1">
                {CurrencyFormatter.centsToFormatted(dashboardData.totalRevenue || 0)}
              </h4>
              <p className="text-muted mb-0">Faturamento Total</p>
              <small className="text-success">
                <Icon icon="mdi:trending-up" className="me-1" />
                Crescimento constante
              </small>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3} className="mb-3">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="d-flex align-items-center justify-content-center mb-3">
                <div className="bg-warning bg-opacity-10 rounded-circle p-3">
                  <Icon icon="mdi:account-group" className="text-warning" style={{ fontSize: '2rem' }} />
                </div>
              </div>
              <h4 className="text-warning mb-1">
                {CurrencyFormatter.centsToFormatted(dashboardData.totalCommissions || 0)}
              </h4>
              <p className="text-muted mb-0">Comissões Pagas</p>
              <small className="text-muted">
                Para afiliados e produtores
              </small>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3} className="mb-3">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="d-flex align-items-center justify-content-center mb-3">
                <div className="bg-info bg-opacity-10 rounded-circle p-3">
                  <Icon icon="mdi:bank" className="text-info" style={{ fontSize: '2rem' }} />
                </div>
              </div>
              <h4 className="text-info mb-1">
                {CurrencyFormatter.centsToFormatted(dashboardData.platformEarnings || 0)}
              </h4>
              <p className="text-muted mb-0">Receita da Plataforma</p>
              <small className="text-muted">
                Taxas e comissões
              </small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Linha branca de separação */}
      <hr className="my-4" style={{ borderColor: 'white', borderWidth: '2px' }} />

      {/* Gráficos */}
      <Row className="mb-4">
        <Col lg={8} className="mb-4">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Header className="bg-white border-0">
              <h5 className="mb-0">
                <Icon icon="mdi:chart-line" className="me-2 text-primary" />
                Evolução das Vendas e Comissões
              </h5>
            </Card.Header>
            <Card.Body>
              <div style={{ height: '350px' }}>
                <Line data={lineChartData} options={{
                  ...chartOptions,
                  scales: {
                    ...chartOptions.scales,
                    y: {
                      ...chartOptions.scales.y,
                      ticks: {
                        callback: function(value) {
                          return 'R$ ' + value.toLocaleString('pt-BR');
                        }
                      }
                    }
                  }
                }} />
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4} className="mb-4">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Header className="bg-white border-0">
              <h5 className="mb-0">
                <Icon icon="mdi:chart-donut" className="me-2 text-primary" />
                Distribuição de Receitas
              </h5>
            </Card.Header>
            <Card.Body>
              <div style={{ height: '350px' }}>
                <Doughnut data={doughnutData} options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                    },
                    tooltip: {
                      callbacks: {
                        label: function(context) {
                          return context.label + ': R$ ' + context.parsed.toLocaleString('pt-BR');
                        }
                      }
                    }
                  }
                }} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Linha branca de separação */}
      <hr className="my-4" style={{ borderColor: 'white', borderWidth: '2px' }} />

      <Row className="mb-4">
        <Col lg={6} className="mb-4">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Header className="bg-white border-0">
              <h5 className="mb-0">
                <Icon icon="mdi:chart-bar" className="me-2 text-primary" />
                Top Produtores
              </h5>
            </Card.Header>
            <Card.Body>
              <div style={{ height: '300px' }}>
                <Bar data={barChartData} options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false,
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                    }
                  }
                }} />
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6} className="mb-4">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Header className="bg-white border-0">
              <h5 className="mb-0">
                <Icon icon="mdi:account-star" className="me-2 text-primary" />
                Top Afiliados
              </h5>
            </Card.Header>
            <Card.Body>
              <div style={{ height: '300px' }}>
                <Bar data={affiliateBarData} options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  indexAxis: 'y',
                  plugins: {
                    legend: {
                      display: false,
                    },
                  },
                  scales: {
                    x: {
                      beginAtZero: true,
                    }
                  }
                }} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Linha branca de separação */}
      <hr className="my-4" style={{ borderColor: 'white', borderWidth: '2px' }} />

      {/* Gráficos de Categorias e Status */}
      <Row className="mb-4">
        <Col lg={6} className="mb-4">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Header className="bg-white border-0">
              <h5 className="mb-0">
                <Icon icon="mdi:chart-pie" className="me-2 text-primary" />
                Categorias de Produtos
              </h5>
            </Card.Header>
            <Card.Body>
              <div style={{ height: '300px' }}>
                <Doughnut data={categoryPieData} options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                    }
                  }
                }} />
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6} className="mb-4">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Header className="bg-white border-0">
              <h5 className="mb-0">
                <Icon icon="mdi:chart-donut-variant" className="me-2 text-primary" />
                Status das Vendas
              </h5>
            </Card.Header>
            <Card.Body>
              <div style={{ height: '300px' }}>
                <Doughnut data={statusDonutData} options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                    }
                  }
                }} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Linha branca de separação */}
      <hr className="my-4" style={{ borderColor: 'white', borderWidth: '2px' }} />

      {/* Seção de Aprovação de Contas Bancárias */}
      <BankAccountsApprovalSection />

    </div>
  );
};

export default AdminDashboard;